/**
 * Asaas Wallet Management API
 *
 * APIs para resellers gerenciarem suas carteiras digitais Asaas BaaS.
 */

import api from './api'

// ============================================================================
// Types
// ============================================================================

export type WalletBalance = {
  organization_id: string
  organization_name: string
  wallet_id: string | null
  balance: number
  currency: string
  last_updated: string
  /** false = organização ainda sem conta Asaas (estado normal, não erro). */
  configured: boolean
}

export type CreateAccountRequest = {
  cnpj: string
  email: string
  phone: string
  mobile_phone: string
  address: string
  address_number: string
  province: string
  postal_code: string
  complement?: string
  company_type: string
  site?: string
  income_value: number
}

export type CreateAccountResponse = {
  message: string
  account_id: string
  wallet_id: string
  organization_id: string
}

export type WalletStats = {
  total_received: number
  total_pending: number
  total_overdue: number
  transaction_count: number
  last_30_days_received: number
  currency: string
}

export type PaymentSplit = {
  id: string
  contract_id: string
  contract_name: string
  reseller_id: string
  reseller_name: string
  cloud_provider_id: string
  cloud_provider_name: string
  reseller_percentage: number
  cloud_provider_percentage: number
  reseller_wallet_id: string
  cloud_provider_wallet_id: string
  is_active: boolean
  created_at: string
}

export type CreatePaymentSplitRequest = {
  contract_id: string
  reseller_percentage: number
  cloud_provider_percentage: number
}

export type TransactionSummary = {
  id: string
  vm_instance_id: string | null
  contract_id: string
  customer_id: string
  customer_name: string
  gross_amount: number
  net_amount: number
  fee_amount: number
  currency: string
  splits: Record<string, number>
  status: string
  payment_method: string | null
  due_date: string | null
  confirmed_date: string | null
  received_date: string | null
  created_at: string
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Consulta saldo da carteira digital Asaas
 */
export const getWalletBalance = async (): Promise<WalletBalance> => {
  const response = await api.get('/api/v1/asaas/wallet/balance')
  return response.data
}

/**
 * Cria conta Asaas white-label para o reseller
 */
export const createAsaasAccount = async (
  data: CreateAccountRequest,
): Promise<CreateAccountResponse> => {
  const response = await api.post('/api/v1/asaas/wallet/account', data)
  return response.data
}

/**
 * Consulta estatísticas da carteira
 */
export const getWalletStats = async (): Promise<WalletStats> => {
  const response = await api.get('/api/v1/asaas/wallet/stats')
  return response.data
}

/**
 * Lista configurações de split de pagamentos
 */
export const listPaymentSplits = async (params?: {
  skip?: number
  limit?: number
  contract_id?: string
}): Promise<PaymentSplit[]> => {
  const response = await api.get('/api/v1/asaas/splits', { params })
  return response.data
}

/**
 * Cria configuração de split para um contrato
 */
export const createPaymentSplit = async (
  data: CreatePaymentSplitRequest,
): Promise<PaymentSplit> => {
  const response = await api.post('/api/v1/asaas/splits', data)
  return response.data
}

/**
 * Lista histórico de transações
 */
export const listTransactions = async (params?: {
  skip?: number
  limit?: number
  status_filter?: string
  contract_id?: string
}): Promise<TransactionSummary[]> => {
  const response = await api.get('/api/v1/asaas/transactions', { params })
  return response.data
}

// ============================================================================
// Export default object
// ============================================================================

const asaasWalletApi = {
  getWalletBalance,
  createAsaasAccount,
  getWalletStats,
  listPaymentSplits,
  createPaymentSplit,
  listTransactions,
}

export default asaasWalletApi
